import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import MoreVert from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import ViewRedemptionsModal from "./Modal/ViewRedemptionsModal";
import { Pagination, useMediaQuery, useTheme } from "@mui/material";
import { Edit, Delete, Add, QrCode2 } from "@mui/icons-material";

import VoucherHero from "./VoucherHero";
import VoucherFunction from "./VoucherFunction";
import VoucherQR from "./VoucherQR";
import { ExportButtons } from "./VoucherExport";
import { getAuthCredentials } from "../../utils/auth";
import TitleSetter from "../../component/TitleSetter";
import Swal from "sweetalert2";

const Voucher = () => {
  const [vouchers, setVouchers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isQROpen, setIsQROpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuVoucher, setMenuVoucher] = useState(null);
  const [isRedemptionsOpen, setIsRedemptionsOpen] = useState(false);
  const [redemptionVoucherId, setRedemptionVoucherId] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { token } = getAuthCredentials();
  const navigate = useNavigate();

  /* ────────────────────────────────────────────────────────────
   * Data fetch
   * ──────────────────────────────────────────────────────────── */
  useEffect(() => {
    fetchVouchers();
  }, [searchQuery, page, limit]);

  const fetchVouchers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/vouchers/fetch-vouchers`,
        {
          params: { page, limit, search: searchQuery || undefined },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const { vouchers, totalPages } = response.data;
      setVouchers(vouchers);
      setTotalPages(totalPages);
    } catch (error) {
      console.error("Error fetching vouchers:", error);
      setVouchers([]);
    } finally {
      setLoading(false);
    }
  };

  /* ────────────────────────────────────────────────────────────
   * Helpers
   * ──────────────────────────────────────────────────────────── */
  const resolveRoleLabel = (roleField) => {
    if (!roleField || roleField === "None") return "Customer";
    if (roleField === "All") return "All Roles";
    if (Array.isArray(roleField)) return roleField.join(", ");
    return roleField; // single ObjectId string
  };

  const filterBySearch = (list) => {
    if (!searchQuery) return list;
    const q = searchQuery.toLowerCase();
    return list.filter((v) =>
      `${v.name} ${v.voucherType} ${v.voucherCode} ${resolveRoleLabel(
        v.voucherRoleName
      )}`
        .toLowerCase()
        .includes(q)
    );
  };

  const groupByRole = (list) =>
    list.reduce((acc, v) => {
      const key = resolveRoleLabel(v.voucherRoleName);
      (acc[key] = acc[key] || []).push(v);
      return acc;
    }, {});

  /* ────────────────────────────────────────────────────────────
   * Handlers
   * ──────────────────────────────────────────────────────────── */
  const handleMenuOpen = (event, voucher) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuVoucher(voucher);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuVoucher(null);
  };

  const handleEditVoucher = (voucher) => {
    setSelectedVoucher(voucher);
    setIsEditOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditOpen(false);
    setSelectedVoucher(null);
  };

  const handleOpenAddModal = () => {
    setSelectedVoucher(null);
    setIsAddOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddOpen(false);
  };

  const handleViewRedemptions = () => {
    if (menuVoucher) {
      setRedemptionVoucherId(menuVoucher._id);
      setIsRedemptionsOpen(true);
    }
    handleMenuClose();
  };

  const handleCloseRedemptions = () => {
    setIsRedemptionsOpen(false);
    setRedemptionVoucherId(null);
  };

  const handleDelete = async (voucherId) => {
    try {
      const confirmResult = await Swal.fire({
        title: "Are you sure?",
        text: "This voucher will be deleted permanently!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
      });

      if (confirmResult.isConfirmed) {
        await axios.delete(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/vouchers/delete-voucher/${voucherId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        Swal.fire("Deleted!", "The voucher has been deleted.", "success");
        fetchVouchers();
      }
    } catch (error) {
      console.error("Error deleting voucher:", error);
      Swal.fire("Error!", "Failed to delete the voucher.", "error");
    }
  };

  const handleViewQR = (voucher) => {
    setSelectedVoucher(voucher);
    setIsQROpen(true);
  };

  const orderedGroups = Object.entries(
    groupByRole(filterBySearch(vouchers))
  ).sort(([a], [b]) => {
    if (a === "Customer") return 1;
    if (b === "Customer") return -1;
    return a.localeCompare(b);
  });

  /* ────────────────────────────────────────────────────────────
   * Render
   * ──────────────────────────────────────────────────────────── */
  return (
    <Box sx={{ padding: 3 }}>
      <TitleSetter title="Vouchers" />
      <VoucherHero activeTab="details" />

      {/* top controls */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 2,
          justifyContent: isMobile ? "flex-start" : "center",
          mb: 3,
          alignItems: isMobile ? "stretch" : "center",
        }}
      >
        <TextField
          label="Search Voucher"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
          fullWidth
          sx={{ maxWidth: isMobile ? "100%" : 250 }}
        />

        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenAddModal}
          sx={{
            width: isMobile ? "100%" : "auto",
            bgcolor: "#4CAF50",
            "&:hover": { bgcolor: "#388E3C" },
          }}
        >
          Add Voucher
        </Button>

        <ExportButtons data={vouchers} />
      </Box>

      {/* main table */}
      <Paper elevation={3} sx={{ p: 2, overflowX: "auto" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {orderedGroups.map(([roleLabel, rows]) => (
              <Accordion key={roleLabel} defaultExpanded sx={{ mb: 3 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={600}>{roleLabel}</Typography>
                </AccordionSummary>

                <AccordionDetails>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Minimum Purchase</TableCell>
                        <TableCell>Amount Deduction</TableCell>
                        <TableCell>Max Deduction</TableCell>
                        <TableCell>Code</TableCell>
                        <TableCell>Times Used</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {rows.map((voucher) => (
                        <TableRow
                          key={voucher._id}
                          sx={{
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: "rgba(0,0,0,0.1)",
                            },
                          }}
                        >
                          <TableCell>{voucher.name}</TableCell>
                          <TableCell>{voucher.voucherType}</TableCell>
                          <TableCell>{voucher.minimumDeduct}</TableCell>
                          <TableCell>
                            {voucher.amountDeduct}
                            {voucher.voucherType === "Percentage Deduction"
                              ? " %"
                              : ""}
                          </TableCell>
                          <TableCell>{voucher.maxDeduct || "N/A"}</TableCell>
                          <TableCell>{voucher.voucherCode}</TableCell>
                          <TableCell>{voucher.numberOfTimesUsed}</TableCell>
                          <TableCell>
                            <IconButton
                              color="primary"
                              onClick={() => handleEditVoucher(voucher)}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              color="secondary"
                              onClick={() => handleViewQR(voucher)}
                            >
                              <QrCode2 />
                            </IconButton>
                            <IconButton
                              onClick={(e) => handleMenuOpen(e, voucher)}
                            >
                              <MoreVert />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            ))}

            {/* pagination */}
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "center",
                alignItems: "center",
                mt: 3,
                gap: isMobile ? 1 : 3,
              }}
            >
              <FormControl
                sx={{
                  maxWidth: isMobile ? "100%" : 80,
                  "& .MuiInputBase-root": { height: 30, fontSize: "0.85rem" },
                  "& .MuiSelect-select": { p: "5px 10px" },
                }}
                fullWidth
              >
                <InputLabel sx={{ fontSize: "0.85rem" }}>Rows</InputLabel>
                <Select
                  value={limit}
                  onChange={(e) => {
                    setLimit(parseInt(e.target.value, 10));
                    setPage(1);
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>

              <Pagination
                count={totalPages}
                page={page}
                onChange={(event, value) => setPage(value)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>

            {/* modals */}
            {selectedVoucher && (
              <VoucherFunction
                open={isEditOpen}
                handleClose={handleCloseEditModal}
                voucher={selectedVoucher}
                refreshData={fetchVouchers}
                setIsEditOpen={setIsEditOpen}
                setIsAddOpen={setIsAddOpen}
              />
            )}
            <VoucherFunction
              open={isAddOpen}
              handleClose={handleCloseAddModal}
              voucher={null}
              refreshData={fetchVouchers}
              setIsEditOpen={setIsEditOpen}
              setIsAddOpen={setIsAddOpen}
            />
            <VoucherQR
              open={isQROpen}
              voucher={selectedVoucher}
              onClose={() => setIsQROpen(false)}
            />
          </>
        )}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          {menuVoucher &&
            menuVoucher.voucherRoleType !== undefined &&
            menuVoucher.voucherRoleType !== null &&
            menuVoucher.voucherRoleType !== "None" && (
              <MenuItem onClick={handleViewRedemptions}>
                View Redemptions
              </MenuItem>
            )}
          <MenuItem
            onClick={() => {
              handleDelete(menuVoucher._id);
              handleMenuClose();
            }}
            sx={{ color: "error.main" }}
          >
            Delete
          </MenuItem>
        </Menu>
        <ViewRedemptionsModal
          open={isRedemptionsOpen}
          onClose={handleCloseRedemptions}
          voucherId={redemptionVoucherId}
        />
      </Paper>
    </Box>
  );
};

export default Voucher;
